import React, { FC } from 'react';
import { Tooltip } from 'antd';
import AviaryIcon from 'assets/images/icons/aviary.svg';
import LeafIcon from 'assets/images/icons/leaf.svg';
import MtbIcon from 'assets/images/icons/mtb.svg';
import TreesIcon from 'assets/images/icons/trees.svg';
import FlowerIcon from 'assets/images/icons/flower.svg';
import PicnicIcon from 'assets/images/icons/picnic.svg';
import KayakIcon from 'assets/images/icons/kayak.svg';
import BalsaIcon from 'assets/images/icons/balsa.svg';
import * as texts from 'assets/texts/ticketFeatures';
import styles from './FeatureIcon.module.scss';

type IconAsset = {
  src: string;
  scale: number;
};

// Mirrors nameToIcon in tricao-front-v2 FeatureIcon; `scale` normalises each
// file's own padding so every glyph reads at the size aviary does.
const slugToIcon: Record<string, IconAsset> = {
  aviario: { src: AviaryIcon, scale: 1 },
  'jardin-botanico': { src: LeafIcon, scale: 1 },
  'circuito-mtb': { src: MtbIcon, scale: 0.86 },
  'humedal-giverny': { src: TreesIcon, scale: 0.71 },
  'plaza-la-virgen-senderos': { src: FlowerIcon, scale: 0.69 },
  'zonas-picnic-miradores': { src: PicnicIcon, scale: 1 },
  kayak: { src: KayakIcon, scale: 1 },
  balsa: { src: BalsaIcon, scale: 1 },
};

const DEFAULT_SIZE = 32;

export const hasIcon = (slug: string): boolean => Boolean(slugToIcon[slug]);

type FeatureIconProps = {
  slug: string;
  description: string;
  size?: number;
};

const FeatureIcon: FC<FeatureIconProps> = ({
  slug,
  description,
  size,
}: FeatureIconProps) => {
  const circleSize = size || DEFAULT_SIZE;
  const asset = slugToIcon[slug];

  if (!asset) {
    return (
      <Tooltip title={texts.missingIconTooltip}>
        <span
          className={styles.Missing}
          style={{ height: circleSize, width: circleSize }}
          role="img"
          aria-label={texts.missingIconLabel}
        >
          ?
        </span>
      </Tooltip>
    );
  }

  const iconSize = Math.round(circleSize * asset.scale);

  return (
    <Tooltip title={description}>
      <span
        className={styles.Circle}
        style={{ height: circleSize, width: circleSize }}
        role="img"
        aria-label={description}
      >
        <img
          className={styles.Icon}
          src={asset.src}
          style={{ height: iconSize, width: iconSize }}
          alt=""
        />
      </span>
    </Tooltip>
  );
};

FeatureIcon.defaultProps = {
  size: DEFAULT_SIZE,
};

export default FeatureIcon;
